import type React from 'react';
import type { Project } from '../types';
import type { FormState } from './ProjectForm';
import { milestoneHealth, HEALTH_TITLE_KEY, HEALTH_BADGE_CLASS } from '../utils/executionHealth';
import { RANK, type ProjectTopic } from '../utils/stages';
import { useStageName } from './ProjectStageBar';
import { ProjectSection } from './ProjectSection';
import { effectiveProjectRole, getProjectRole } from '../proposals/roles-projet/projectRoles';
import { useT } from '../i18n';

// The project's landing tab: a read-only summary of the whole project — where
// it stands, its milestones and deliverables, what it is, who works on it and
// the decisions taken. Each section opens the tab where it is edited.

interface Props {
  form: FormState;
  project: Project;
  currentLabel: string;
  rank: number;
  domainLabel: string | null;
  levelLabel: string | null;
  canEdit: boolean;
  /** Opens a tab, scrolled to the given card (by id) or to the tab's top. */
  onJump: (topic: ProjectTopic, anchorId?: string) => void;
}

function ymdToDate(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(ymd || '');
  return m ? new Date(+m[1], +m[2] - 1, m[3] ? +m[3] : 1) : null;
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type MsState = 'done' | 'active' | 'late' | 'pending';

export function ProjectSynthesis({ form, project, currentLabel, rank, domainLabel, levelLabel, canEdit, onJump }: Props) {
  const { t, lang } = useT();
  const stageName = useStageName();
  const locale = lang === 'fr' ? 'fr-CH' : 'en-GB';
  const fmtDay = (ymd: string) => ymdToDate(ymd)?.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) ?? '';
  const fmtMonth = (ym: string) => ymdToDate(ym)?.toLocaleDateString(locale, { month: 'long', year: 'numeric' }) ?? '';

  const later = (stage: string) => <span className="synth-later">{t('projectDetail.synth.atStage', { stage: stageName(stage) })}</span>;
  const dash = <span className="synth-empty">—</span>;
  const text = (v: string) => (v.trim() ? <span className="synth-text">{v}</span> : dash);
  const open = (topic: ProjectTopic, anchorId?: string) => (
    <button type="button" className="btn btn-ghost btn-sm" onClick={() => onJump(topic, anchorId)}>
      {t(canEdit && topic !== 'plan' ? 'projectDetail.synth.edit' : 'projectDetail.synth.open')}
    </button>
  );
  const row = (label: string, value: React.ReactNode) => (<><dt>{label}</dt><dd>{value}</dd></>);

  // ── Milestones ──
  const today = todayYmd();
  const milestones = form.milestones.filter(m => m.status !== 'cancelled');
  const completed = milestones.filter(m => m.status === 'completed').length;
  const next = milestones.find(m => m.status !== 'completed');
  const msState = (m: typeof milestones[number]): MsState =>
    m.status === 'completed' ? 'done'
      : m.estimatedEnd && m.estimatedEnd < today ? 'late'
      : m.status === 'active' ? 'active' : 'pending';
  const deliverables = form.deliverables.filter(d => d.title.trim());
  const planEnd = [...milestones].map(m => m.estimatedEnd).filter(Boolean).sort().pop() || '';

  // The project's status from its milestones, while it runs (see executionHealth).
  const health = rank === RANK.execution ? milestoneHealth({ milestones: form.milestones }) : null;
  const healthView = rank < RANK.execution
    ? later('Execution')
    : health
      ? <span className={`badge ${HEALTH_BADGE_CLASS[health.level]}`} title={health.milestone}>{t(HEALTH_TITLE_KEY[health.level])}</span>
      : dash;

  const team = form.allocations.length > 0
    ? form.allocations.map(a => {
      const role = getProjectRole(effectiveProjectRole(a.name, a.projectRole, form.sponsor));
      return { name: a.name, role: role ? t(role.labelKey) : '', pct: a.allocationPct };
    })
    : form.collaborators.map(c => ({ name: c.name, role: '', pct: '' }));

  return (
    <div className="synth">
      {rank >= RANK.planning && <dl className="synth-facts">
        <div className="synth-fact">
          <dt>{t('projectDetail.synth.progress')}</dt>
          <dd>
            {rank < RANK.planning ? later('Planning') : milestones.length === 0 ? dash : (<>
              {t('projectDetail.synth.milestonesCount', { done: completed, total: milestones.length })}
              <span className="synth-meter"><span style={{ width: `${Math.round(100 * completed / milestones.length)}%` }} /></span>
            </>)}
          </dd>
        </div>
        <div className="synth-fact">
          <dt>{t('projectDetail.synth.nextMilestone')}</dt>
          <dd>
            {rank < RANK.planning ? later('Planning')
              : next ? <><span className="synth-ellipsis" title={next.name}>{next.name || '—'}</span>{next.estimatedEnd && <span className="synth-sub">{fmtDay(next.estimatedEnd)}</span>}</>
              : milestones.length ? t('projectDetail.synth.allMilestonesDone') : dash}
          </dd>
        </div>
        <div className="synth-fact">
          <dt>{t('projectDetail.synth.plannedEnd')}</dt>
          <dd>
            {planEnd ? fmtDay(planEnd)
              : form.timeHorizonEnd ? fmtMonth(form.timeHorizonEnd)
              : rank < RANK.scoping ? later('Scoping') : dash}
          </dd>
        </div>
        <div className="synth-fact">
          <dt>{t('projectDetail.synth.health')}</dt>
          <dd>{healthView}</dd>
        </div>
      </dl>}

      <ProjectSection title={t('projectDetail.synth.milestones')} actions={rank >= RANK.planning ? open('plan') : undefined} flush={milestones.length > 0}>
        {rank < RANK.planning && milestones.length === 0 ? (
          <p className="synth-note">{t('projectDetail.synth.milestonesLater', { stage: stageName('Planning') })}</p>
        ) : milestones.length === 0 ? (
          <p className="synth-note">{t('projectDetail.synth.noMilestones')}</p>
        ) : (
          <ol className="synth-ms">
            {milestones.map((m, i) => {
              const state = msState(m);
              return (
                <li key={m.id} className={`synth-ms-row is-${state}`}>
                  <span className="synth-ms-mark" aria-hidden />
                  <span className="synth-ms-main">
                    <span className="synth-ms-name"><span className="synth-ms-n">{i + 1}</span>{m.name || '—'}</span>
                  </span>
                  <span className="synth-ms-dates">
                    {m.estimatedStart || m.estimatedEnd ? `${fmtDay(m.estimatedStart) || '?'} → ${fmtDay(m.estimatedEnd) || '?'}` : '—'}
                  </span>
                  <span className="synth-ms-state">{t(`projectDetail.synth.ms.${state}`)}</span>
                </li>
              );
            })}
          </ol>
        )}
      </ProjectSection>

      <ProjectSection title={t('projectDetail.topics.deliverables')} actions={open('contenu', 'blk-deliverables')} flush={deliverables.length > 0}>
        {deliverables.length === 0 ? <p className="synth-note">{t('projectDetail.synth.noDeliverables')}</p> : (
          <ol className="synth-deliv">
            {deliverables.map((d, i) => {
              const by = milestones.map((m, mi) => m.acceptanceDeliverables.includes(d.id) ? mi + 1 : 0).filter(Boolean);
              return (
                <li key={d.id}>
                  <span className="synth-deliv-n">{i + 1}</span>
                  <div className="synth-deliv-main">
                    <div className="synth-deliv-title">{d.title}</div>
                    {d.body?.trim() && <div className="synth-deliv-body">{d.body}</div>}
                  </div>
                  <span className="synth-deliv-by">{by.length ? t('projectDetail.synth.coveredBy', { list: by.join(', ') }) : ''}</span>
                </li>
              );
            })}
          </ol>
        )}
      </ProjectSection>

      <ProjectSection title={t('projectDetail.synth.projectBlock')} actions={open('contenu', 'blk-problemGoal')}>
        <dl className="synth-dl">
          {row(t('projectDetail.overview.problemLabel'), text(form.problem))}
          {row(t('projectDetail.overview.goalLabel'), text(form.goal))}
          {row(t('projectDetail.overview.valueAddedLabel'), form.valueAdded.length ? form.valueAdded.join(' · ') : dash)}
          {row(t('projectDetail.synth.scopeIn'), rank < RANK.scoping ? later('Scoping') : text(form.scopeIncludes))}
          {row(t('projectDetail.synth.scopeOut'), rank < RANK.scoping ? later('Scoping') : text(form.scopeExcludes))}
          {row(t('projectDetail.synth.classification'), (domainLabel || levelLabel) ? [domainLabel, levelLabel].filter(Boolean).join(' · ') : dash)}
        </dl>
      </ProjectSection>

      <ProjectSection title={t('projectDetail.topic.equipe')} actions={open('equipe', 'blk-ownership')}>
        <dl className="synth-dl">
          {row(t('projectDetail.overview.sponsorLabel'), form.sponsor || dash)}
          {row(t('projectDetail.overview.pmoAccompanistLabel'), form.pmoAccompanist || dash)}
          {row(t('projectDetail.synth.team'), team.length ? (
            <table className="synth-team">
              <tbody>
                {team.map((m, i) => (
                  <tr key={i}>
                    <td>{m.name}</td>
                    <td className="synth-team-role">{m.role}</td>
                    <td className="synth-team-pct">{m.pct ? `${m.pct} %` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : dash)}
          {row(t('projectDetail.synth.horizon'), rank < RANK.scoping
            ? (form.timeline ? <>{form.timeline} <span className="synth-sub">{t('projectDetail.synth.requested')}</span></> : later('Scoping'))
            : (form.timeHorizonStart || form.timeHorizonEnd) ? `${fmtMonth(form.timeHorizonStart) || '?'} → ${fmtMonth(form.timeHorizonEnd) || '?'}` : dash)}
          {row(t('projectDetail.synth.budgetRequested'), form.cash || dash)}
          {row(t('projectDetail.synth.budgetAvailable'), rank < RANK.scoping ? later('Scoping') : (form.scopeAvailableBudget || dash))}
          {row(t('projectDetail.overview.effortSizeLabel'), form.effortSize || dash)}
        </dl>
      </ProjectSection>

      {rank >= RANK.prequalification && (
        <ProjectSection title={t('projectDetail.synth.decisions')} actions={open('strategie', 'blk-strategic')}>
          <dl className="synth-dl">
            {row(t('projectDetail.stage.prequalification'), (<>
              {form.prequalVerdict === 'advance' ? t('projectDetail.prequalDecision.verdictAdvance')
                : form.prequalVerdict === 'defer' || currentLabel === 'Deferred' ? t('projectDetail.prequalDecision.verdictDeferred')
                : rank >= RANK.scoping ? t('projectDetail.prequalDecision.verdictAdvance')
                : currentLabel === 'Préqualification' ? t('projectDetail.synth.pending') : '—'}
              {form.rcajo?.is_strategic && <> <span className="badge badge-ok">{t('prequal.strategic')}</span></>}
            </>))}
            {rank >= RANK.qualification && row(t('projectDetail.stage.qualification'), project.qualified_at
              ? <>{project.qualification_outcome === 'backlog' ? t('projectDetail.qualification.badgeBacklogged') : t('projectDetail.qualification.badgeQualified')} <span className="synth-sub">{new Date(project.qualified_at).toLocaleDateString(locale)}</span></>
              : t('projectDetail.qualification.pending'))}
          </dl>
        </ProjectSection>
      )}
    </div>
  );
}
